'use client'

import React, { useEffect, useState } from 'react'

import { Box, Container, Typography, Card, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import AOS from 'aos'
import 'aos/dist/aos.css'

const StyledFacilityCard = styled(Card)({
  borderRadius: 24,
  overflow: 'hidden',
  height: 320,
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.2)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  position: 'relative',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-12px)',
    boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
    '& .facility-icon': {
      transform: 'scale(1.2) rotate(10deg)'
    },
    '& .facility-content': {
      transform: 'translateY(-8px)'
    }
  }
})

const FacilityGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  }
})

const Facility = () => {
  const [activeCategory, setActiveCategory] = useState('all')

  const facilities = [
    {
      title: 'Ruang Persemayaman',
      description: 'Ruang khusus untuk persemayaman jenazah yang nyaman dan tenang',
      icon: '🛏️',
      category: 'room',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: 'Ruang Pemandian / Pemulasaraan Jenazah',
      description: 'Fasilitas lengkap dan higienis untuk pemandian atau pemulasaraan jenazah',
      icon: '🧼',
      category: 'service',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      title: 'Dekorasi',
      description: 'Dekorasi elegan dan premium sesuai kebutuhan keluarga',
      icon: '🌸',
      category: 'service',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: 'Dokumentasi',
      description: 'Layanan dokumentasi profesional untuk mengabadikan momen',
      icon: '📷',
      category: 'service',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      title: 'Mobil Jenazah',
      description: 'Armada mobil jenazah yang bersih dan terawat',
      icon: '🚗',
      category: 'transport',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      title: 'Area Parkir',
      description: 'Area parkir luas dan aman untuk pelayat',
      icon: '🅿️',
      category: 'facility',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    },
    {
      title: 'Ruang Tunggu',
      description: 'Ruang tunggu nyaman dengan fasilitas lengkap',
      icon: '🪑',
      category: 'room',
      gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)'
    },
    {
      title: 'Sound System',
      description: 'Audio berkualitas tinggi untuk prosesi',
      icon: '🔊',
      category: 'facility',
      gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)'
    },
    {
      title: 'Kamar Mandi',
      description: 'Kamar mandi bersih dan terawat untuk kenyamanan tamu',
      icon: '🚿',
      category: 'facility',
      gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)'
    },
    {
      title: 'Gudang Peti',
      description: 'Gudang khusus penyimpanan peti jenazah yang aman dan rapi',
      icon: '📦',
      category: 'facility',
      gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'
    },
    {
      title: 'TV LCD',
      description: 'Setiap ruangan dilengkapi TV LCD untuk kenyamanan keluarga',
      icon: '📺',
      category: 'facility',
      gradient: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)'
    }
  ]

  const categories = [
    { id: 'all', label: 'Semua Fasilitas', count: facilities.length },
    { id: 'room', label: 'Ruangan', count: facilities.filter(f => f.category === 'room').length },
    { id: 'service', label: 'Layanan', count: facilities.filter(f => f.category === 'service').length },
    { id: 'facility', label: 'Fasilitas', count: facilities.filter(f => f.category === 'facility').length },
    { id: 'transport', label: 'Transportasi', count: facilities.filter(f => f.category === 'transport').length }
  ]

  const filteredFacilities =
    activeCategory === 'all' ? facilities : facilities.filter(facility => facility.category === activeCategory)

  useEffect(() => {
    AOS.init({
      duration: 600,
      once: true,
      easing: 'ease-out-cubic'
    })
  }, [])

  return (
    <Box
      component='section'
      sx={{
        py: { xs: 6, md: 10 },
        background: '#f8f9fa',
        position: 'relative',
        minHeight: '100vh',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Cpath d="M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.4
        }
      }}
      id='facilities'
    >
      <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box textAlign='center' mb={6} data-aos='fade-down'>
          <Typography
            id='facilities-title'
            data-aos={'fade-down'}
            variant='h2'
            component='h2'
            gutterBottom
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'text.primary',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px'
              }
            }}
          >
            Fasilitas Lengkap & Modern
          </Typography>

          <Typography
            data-aos={'fade-down'}
            variant='h6'
            component='p'
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              maxWidth: '700px',
              mx: 'auto',
              color: 'text.secondary',
              lineHeight: 1.6
            }}
          >
            Kami menyediakan fasilitas lengkap dan modern untuk memastikan kenyamanan keluarga dan pelayat
          </Typography>
        </Box>

        {/* Facilities Grid */}
        <FacilityGrid data-aos='fade-up' data-aos-delay='200'>
          {filteredFacilities.map((facility, index) => (
            <StyledFacilityCard
              key={`${facility.category}-${index}`}
              sx={{
                background: `linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18)), ${facility.gradient}`, // overlay lebih tipis
                animationDelay: `${index * 0.1}s`
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative'
                }}
              >
                {/* Icon */}
                <Box
                  className='facility-icon'
                  sx={{
                    fontSize: '4rem',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                  }}
                >
                  {facility.icon}
                </Box>

                {/* Content */}
                <Box className='facility-content' sx={{ transition: 'all 0.3s ease' }}>
                  <Typography
                    variant='h4'
                    sx={theme => ({
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                      fontSize: { xs: '0.98rem', sm: '1.08rem', md: '1.25rem' },
                      mb: 1.2,
                      textShadow: '0 4px 16px rgba(0,0,0,0.22), 0 1.5px 4px rgba(0,0,0,0.13)'
                    })}
                  >
                    {facility.title}
                  </Typography>

                  <Typography
                    variant='body2'
                    sx={theme => ({
                      color: theme.palette.primary.contrastText,
                      lineHeight: 1.5,
                      fontSize: { xs: '0.78rem', sm: '0.89rem', md: '0.98rem' },
                      textShadow: '0 2px 8px rgba(0,0,0,0.13), 0 1px 2px rgba(0,0,0,0.09)'
                    })}
                  >
                    {facility.description}
                  </Typography>
                </Box>

                {/* Category Badge */}
                <Chip
                  label={categories.find(c => c.id === facility.category)?.label}
                  size='small'
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'white',
                    fontSize: '0.8rem',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </Box>
            </StyledFacilityCard>
          ))}
        </FacilityGrid>

        {/* Bottom CTA */}
        <Box textAlign='center' mt={8} data-aos='fade-up' data-aos-delay='400'>
          <Typography
            variant='h3'
            sx={theme => ({
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
              textShadow: '0 4px 20px rgba(0,0,0,0.10)'
            })}
          >
            Siap Melayani 24/7
          </Typography>
          <Typography
            variant='h6'
            sx={theme => ({
              color: theme.palette.text.primary,
              maxWidth: '500px',
              mx: 'auto'
            })}
          >
            Tim profesional dengan fasilitas modern dan lengkap
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default Facility
